import React, { useEffect, useCallback } from "react";
import FormattedMessage from "components/FormattedMessage";
import Spinner, { styles } from "components/Spinner";
import { Button, Flex, Box } from "ui-components";
import history from "utils/history";
import get from "lodash/get";
import isFunction from "lodash/isFunction";
import throttle from "lodash/throttle";
import html2canvas from "html2canvas";
import * as Icons from "components/Icons";
import { gotoUrl, goBack, formatGBtoTBConversion } from "utils/helpers";
import { MANAGE_ORDER_URL } from "utils/constants";
import { COLORS, getButtonColorVariant } from "ui-components/theme";

const PageNavigation = props => {
  const {
    next,
    prev,
    onNextClick,
    form,
    uploadScreenshot,
    screenshotEnabled,
    waitForScreenshot,
    stickyFooter,
    showSummary,
    showMonthlySummary,
    monthlySummaryLabel,
    hideSummaryTitle,
    summaryTitle,
    summaryLabel,
    summaryMessage,
    showCard,
    cardMessage,
    cardInfo,
    totalData,
    totalPrice,
    totalFixedPrice,
    totalPriceWithoutTax,
    totalFixedPriceWithoutTax,
    originalPrice,
    originalFixedPrice,
    originalPriceWithoutTax,
    originalFixedPriceWithoutTax,
    outstandingBillAmount,
    planName,
    planInfo,
    payNowPrice,
    transactionAmount,
    disableNext,
    themeColors,
    fallbackUrl,
    hidePrevButton,
    showPhoneDetails,
    phoneVariant,
    phonePaymentOption,
    showNumberPrice,
    numberPrice,
    loading,
    sim_count,
    screenshot,
    clearScreenshot,
    hidePrevIcon,
    appSettings
  } = props;

  useEffect(() => {
    if (waitForScreenshot && get(screenshot, "doc_id")) {
      onNextClick();
      clearScreenshot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenshot]);

  const onClickHandler = useCallback(
    (...args) => {
      if (screenshotEnabled) {
        const setScreenshotStyles = () => {
          // Update styles to fix screenshot
          const tncCheckBox = tncCheckBoxElement();
          tncCheckBox &&
            tncCheckBox.setAttribute(
              "style",
              "opacity: 1; width: 24px; height: 20px; top: 22px; z-index: 2; display: block; visibility: visible; position: relative;"
            );
          const nav = navElement();
          nav &&
            nav.setAttribute("style", "position: relative; margin-top: 77px;");
        };

        const removeScreenshotStyles = () => {
          const tncCheckBox = tncCheckBoxElement();
          tncCheckBox && tncCheckBox.setAttribute("style", "");
          const nav = navElement();
          nav && nav.setAttribute("style", "");
        };

        window.scrollTo(0, 0);
        console.log(form, " Screenshot Start", new Date());
        setScreenshotStyles();

        html2canvas(document.body)
          .then(canvas => {
            console.log(form, " Screenshot End", new Date());
            return canvas.toDataURL("image/png", 0.1);
          })
          .then(screenshot => {
            console.log(form, " Screenshot Saved", new Date());
            isFunction(uploadScreenshot) &&
              uploadScreenshot({ form, screenshot });

            if (!waitForScreenshot) onNextClick(...args);

            removeScreenshotStyles();
          })
          .catch(e => {
            console.log(form, " Screenshot Save Failed", e);

            if (!waitForScreenshot) {
              onNextClick(...args);
            }

            removeScreenshotStyles();
          });
      } else {
        onNextClick(...args);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [screenshotEnabled, form, uploadScreenshot, waitForScreenshot, onNextClick]
  );

  // Here we throttle the on click so until the store gets updated user can't click multiple times. This will prevent
  // calling multiple API calls.
  const onClick = useCallback(throttle(onClickHandler, 1000), [onClickHandler]);

  const tncCheckBoxElement = () =>
    document.querySelector("input[name='terms_and_conditions_agreed']");

  const navElement = () =>
    document.querySelector("div[data-testid='page-navigation']");

  const showGbConversionToTbEnabled = get(
    appSettings,
    "generic.show_gb_conversion_to_tb_enabled",
    false
  );
  let totalDataTB = totalData;
  if (showGbConversionToTbEnabled) {
    totalDataTB = totalData > 0 ? formatGBtoTBConversion(totalData) : 0;
  }

  const buttonTheme = get(themeColors, "button", {});
  const primaryColor = get(buttonTheme, "color", COLORS.blue[4]);
  const phoneName = get(phoneVariant, "name") || "";
  const phoneSpecs = get(phoneVariant, "specs") || "";
  const phoneNameWithSpecs = get(phoneVariant, "nameWithSpecs") || "";
  const phoneInstallments = get(phoneVariant, "installmentMonths") || "";
  const messageData = {
    originalPrice,
    originalFixedPrice,
    originalPriceWithoutTax,
    originalFixedPriceWithoutTax,
    totalData,
    totalPrice,
    totalFixedPrice,
    totalPriceWithoutTax,
    totalFixedPriceWithoutTax,
    planName,
    transactionAmount,
    payNowPrice,
    sim_count,
    outstandingBillAmount,
    phoneName,
    phoneSpecs,
    phoneNameWithSpecs,
    phoneInstallments,
    totalDataTB
  };

  const summaryLabelLocaleKey = monthlySummaryLabel || summaryLabel;

  const renderPhoneDetails = () => {
    const { transactionAmount = "", pay_monthly_months = 0 } =
      get(phoneVariant, `paymentOption.${phonePaymentOption}`) || {};
    return (
      <Box className="summary" maxWidth="60%">
        <p className="summary-title">{get(phoneVariant, "name")}</p>
        <p className="summary-label" style={{ color: primaryColor }}>
          {transactionAmount}
          {pay_monthly_months > 0 && (
            <span
              className="summary-label-message"
              style={{ color: primaryColor }}
            >
              &nbsp;
              <FormattedMessage
                id={"for_installment_months"}
                values={messageData}
              />
            </span>
          )}
        </p>
        <p className="summary-message">{get(phoneVariant, "specs")}</p>
      </Box>
    );
  };

  const renderSummary = () => (
    <Box className="summary" maxWidth="60%">
      {!hideSummaryTitle && (
        <p className="summary-title">
          {summaryTitle ? (
            <FormattedMessage id={summaryTitle} values={messageData} />
          ) : showMonthlySummary ? (
            <FormattedMessage id={"pay_monthly"} />
          ) : (
            planName
          )}
        </p>
      )}
      <p className="summary-label" style={{ color: primaryColor }}>
        {summaryLabelLocaleKey ? (
          totalData >= 1000 && showGbConversionToTbEnabled ? (
            <FormattedMessage
              id={"total_data_for_price_with_sim_tb"}
              values={messageData}
              html
            />
          ) : (
            <FormattedMessage
              id={summaryLabelLocaleKey}
              values={messageData}
              html
            />
          )
        ) : showMonthlySummary ? (
          totalPrice
        ) : showNumberPrice ? (
          numberPrice
        ) : (
          transactionAmount || payNowPrice
        )}
      </p>
      <p className="summary-message">
        {summaryMessage ? (
          <FormattedMessage id={summaryMessage} values={messageData} />
        ) : (
          planInfo
        )}
      </p>
    </Box>
  );
  if (stickyFooter) {
    window.stickyFooterHeight = 85;
  }
  const ChevronLeftCircleOutline = Icons["ChevronLeftCircleOutline"];
  const prevIconMode = !hidePrevIcon && stickyFooter && window.innerWidth < 420;
  const prevButtonVariant = prevIconMode ? "icon" : "outline";
  const showPrevButton = (prev || stickyFooter) && !hidePrevButton;
  const singleButtonMode = !(
    showPrevButton ||
    showSummary ||
    showMonthlySummary ||
    showPhoneDetails
  );
  const prevPath = get(prev, "path", "");
  const reDirectPath = get(fallbackUrl, "path", MANAGE_ORDER_URL);
  const nextButtonAlign = get(next, "align");
  const nextButtonVariant = get(next, "variant", "primary");
  const nextButtonWidth = get(next, "width", "fit-content");
  const nextButtonIcon = get(next, "icon");
  const nextButtonIconSize = get(next, "iconSize") || "36px";
  const NextButtonIconComponent = Icons[nextButtonIcon];
  const navigateBack = () => {
    const reload = get(prev, "reload", false);
    prevPath
      ? gotoUrl(prevPath, false, {}, reload)
      : history.length > 1
      ? goBack()
      : gotoUrl(reDirectPath, false, {}, reload);
  };

  const PrevButton = ({ children, ...props }) => (
    <Button
      variant={prevButtonVariant}
      onClick={navigateBack}
      data-testid={"prev-button"}
      sx={getButtonColorVariant(buttonTheme, prevButtonVariant)}
      {...props}
    >
      {children}
    </Button>
  );

  const stickyPrevButtonProps = prevIconMode
    ? {}
    : {
        width: "fit-content",
        p: hidePrevIcon ? "0.5rem 1rem" : "2px",
        my: "auto",
        minHeight: hidePrevIcon ? "36px" : "44px",
        minWidth: hidePrevIcon ? "36px" : "120px"
      };

  const renderPrevButton = () =>
    stickyFooter && !singleButtonMode ? (
      <Box
        minWidth={"36px"}
        width={"fit-content"}
        style={{ visibility: !showPrevButton ? "hidden" : "visible" }}
      >
        <PrevButton {...stickyPrevButtonProps}>
          <Flex alignItems={"center"} p={"0.1rem"}>
            {hidePrevIcon ? null : (
              <ChevronLeftCircleOutline size="36px" color={primaryColor} />
            )}
            {prevIconMode ? null : (
              <FormattedMessage id={get(prev, "label", "go_back")} />
            )}
          </Flex>
        </PrevButton>
      </Box>
    ) : showPrevButton ? (
      <PrevButton>
        <FormattedMessage id={get(prev, "label")} />
      </PrevButton>
    ) : null;

  const nextButtonStyles = getButtonColorVariant(
    buttonTheme,
    nextButtonVariant
  );

  const openNewWindowProps = next?.openNewWindow
    ? {
        as: "a",
        target: "_blank",
        href: next?.href || next?.path,
        onClick: () => {}
      }
    : {};

  const NextButton = props =>
    next ? (
      <Button
        onClick={!disableNext ? onClick : () => {}}
        disabled={disableNext}
        data-testid={"next-button"}
        width={nextButtonWidth}
        sx={nextButtonStyles}
        style={{ ...next.styles }}
        {...props}
        padding="0.25rem"
        {...openNewWindowProps}
      >
        <Flex p={0} alignItems="center" width="100%">
          {NextButtonIconComponent && (
            <Box px={2}>
              <NextButtonIconComponent
                size={nextButtonIconSize}
                color={nextButtonStyles.color}
              />
            </Box>
          )}
          <Box
            mx={"auto"}
            p={"0.25rem"}
            pr={NextButtonIconComponent ? "1.5rem" : "0.5rem"}
          >
            {loading ? (
              <Spinner size={19} sx={styles.button} />
            ) : (
              <FormattedMessage id={next.label} />
            )}
          </Box>
        </Flex>
      </Button>
    ) : null;

  const stickyNextButtonProps = {
    width: "fit-content",
    px: "4px",
    py: "8px",
    my: "auto",
    minHeight: "36px",
    minWidth: "100px"
  };

  const renderNextButton = () =>
    stickyFooter ? (
      <Box minWidth={"36px"}>
        <NextButton {...stickyNextButtonProps} />
      </Box>
    ) : (
      <NextButton />
    );

  return (
    <Box
      variant={
        showCard
          ? "borderSection"
          : stickyFooter
          ? "stickyFooter"
          : "transparent"
      }
      data-testid={"page-navigation"}
      sx={
        showCard
          ? {
              color: primaryColor,
              borderColor: primaryColor,
              mb: "2rem"
            }
          : {}
      }
    >
      {showCard && cardMessage && (
        <Flex p="0.5rem" justifyContent="space-around" fontWeight={"700"}>
          <FormattedMessage id={cardMessage} values={messageData} html />
        </Flex>
      )}
      <Flex
        justifyContent={
          singleButtonMode ? nextButtonAlign || "space-evenly" : "space-between"
        }
        alignItems={"center"}
        width={"100%"}
        maxWidth={"680px"}
        margin={"0 auto"}
      >
        {renderPrevButton()}
        {stickyFooter && (showSummary || showMonthlySummary) && renderSummary()}
        {stickyFooter && showPhoneDetails && renderPhoneDetails()}
        {renderNextButton()}
      </Flex>
      {showCard && cardInfo && (
        <Flex
          p="0.5rem"
          justifyContent="space-around"
          fontWeight={"500"}
          fontSize={"0.9rem"}
        >
          <FormattedMessage id={cardInfo} html />
        </Flex>
      )}
    </Box>
  );
};

export default PageNavigation;
